import type { OverviewResponse } from "contracts/api/dashboard/responses";
import { For, Show } from "solid-js";
import { pageDisplayLabel } from "../pages/boundaries/page-label.parser";
import type { CandidateJourney, PriorityContentFlow } from "./journeyTypes";

export function JourneyStructuralEvidence(props: {
  data: OverviewResponse;
  flow: PriorityContentFlow | null;
  journey: CandidateJourney | null;
}) {
  const selected = () =>
    props.flow && props.journey
      ? { flow: props.flow, journey: props.journey }
      : null;
  return (
    <section class="signal-panel journey-evidence">
      <div class="signal-panel-head">
        <div>
          <span class="signal-mini-kicker">Evidence basis</span>
          <h2>Structural evidence</h2>
          <p>
            Concise evidence for the selected current path and representative
            route.
          </p>
        </div>
      </div>
      <Show
        when={selected()}
        keyed
        fallback={
          <p class="signal-muted">Select a current path to review evidence.</p>
        }
      >
        {({ flow, journey }) => (
          <>
            <dl class="journey-evidence-summary">
              <div>
                <dt>Route pages</dt>
                <dd>{journey.steps.length}</dd>
              </div>
              <div>
                <dt>Recorded links</dt>
                <dd>{journey.links.length}</dd>
              </div>
              <div>
                <dt>Pages assessed</dt>
                <dd>{journey.assessedPages}</dd>
              </div>
              <div>
                <dt>Priority concerns</dt>
                <dd>{flow.priorityConcernCount}</dd>
              </div>
            </dl>
            <p class="journey-role-basis">
              Roles inferred from URL, title and recorded link structure.
            </p>
            <details>
              <summary>View recorded links</summary>
              <ul>
                <For each={journey.links}>
                  {(link) => (
                    <Show when={pagesForLink(props.data, link)} keyed>
                      {(pages) => (
                        <li>
                          {pageDisplayLabel(pages.from, props.data.site)} to{" "}
                          {pageDisplayLabel(pages.to, props.data.site)}
                        </li>
                      )}
                    </Show>
                  )}
                </For>
              </ul>
            </details>
            <details>
              <summary>View role inferences</summary>
              <ul>
                <For each={journey.steps}>
                  {(step) => (
                    <li>
                      {pageDisplayLabel(step.page, props.data.site)} /{" "}
                      {step.roleLabel}
                    </li>
                  )}
                </For>
              </ul>
            </details>
            <details>
              <summary>Technical link records</summary>
              <code>
                {journey.links
                  .map((link) => `${link.fromPageId} -> ${link.toPageId}`)
                  .join("\n")}
              </code>
            </details>
          </>
        )}
      </Show>
    </section>
  );
}

function pagesForLink(
  data: OverviewResponse,
  link: OverviewResponse["links"][number],
) {
  const from = data.pages.find((page) => page.id === link.fromPageId);
  const to = data.pages.find((page) => page.id === link.toPageId);
  return from && to ? { from, to } : null;
}
